import { createContext, useContext, useReducer, useRef } from 'react';

import type { ReactElement, ReactNode } from 'react';
import type { SortableStateType } from './sortableReducer';
import type { SortableItem, SortableItemProps } from './components/SortableItem';

import { sortableReducer, initialState } from './sortableReducer';
import { SortableContainer } from './components/SortableContainer';
import * as actions from './actions';

type SortableContextType = {
  clearDraggingSource: () => void;
  clearDraggingTarget: () => void;
  dragEnd: () => void;
  dragStart: (index: number) => void;
  drop: (index: number) => void;
  moveToPosition: (index: number) => void;
  reset: () => void;
  setClone: (node: Element) => void;
  setDraggingSource: (index: number) => void;
  setDraggingTarget: (index: number) => void;
};

const SortableContext = createContext<(SortableContextType & SortableStateType) | undefined>(undefined);

type SortableItems = ReactElement<SortableItemProps, typeof SortableItem>[];

interface Props<T = unknown> {
  renderItems: SortableItems;
  dataItems: T[];
  scrollable?: boolean;
  hideNumbers?: boolean;
  onSort?: (items: T[]) => void;
  children?: ReactNode;
}

function isReactElementWithIndex(value: string | ReactElement): value is ReactElement<{ index: number }> {
  return typeof value !== 'string';
}

const init = (renderItems: SortableItems): SortableStateType => {
  const reactChildren = renderItems.filter(isReactElementWithIndex);

  return {
    ...initialState,
    indices: reactChildren.map(({ props }) => props.index),
    maxIndex: reactChildren.length - 1,
    sortableItems: reactChildren,
  };
};

export function SortableProvider<T>({ renderItems, dataItems, scrollable, hideNumbers, onSort }: Props<T>) {
  const persistentItems = useRef(dataItems);

  const [state, dispatch] = useReducer(sortableReducer, renderItems, init);

  function setDraggingSource(index: number) {
    dispatch(actions.setDraggingSource(index));
  }

  function setDraggingTarget(index: number) {
    dispatch(actions.setDraggingTarget(index));
  }

  function moveToPosition(index: number) {
    dispatch(actions.moveToPosition(index));
  }

  function clearDraggingTarget() {
    dispatch(actions.clearDraggingTarget());
  }

  function clearDraggingSource() {
    dispatch(actions.clearDraggingSource());
  }

  function drop(index: number) {
    dispatch(actions.drop(index));
  }

  function dragStart(index: number) {
    dispatch(actions.dragStart(index));
  }

  function dragEnd() {
    dispatch(actions.dragEnd());
  }

  function reset() {
    dispatch(actions.reset());
  }

  function setClone(node: Element) {
    dispatch(actions.setClone(node));
  }

  function onHandleSort(sortedIndices: number[]) {
    const sortedData = sortedIndices.map((index) => persistentItems.current[index]);
    onSort && onSort(sortedData);
  }

  return (
    <SortableContext.Provider
      value={{
        ...state,
        clearDraggingSource,
        clearDraggingTarget,
        dragEnd,
        dragStart,
        drop,
        moveToPosition,
        reset,
        setClone,
        setDraggingSource,
        setDraggingTarget,
      }}
    >
      <SortableContainer scrollable={scrollable} hideNumbers={hideNumbers} onSort={onHandleSort} />
    </SortableContext.Provider>
  );
}

export function useSortable() {
  const context = useContext(SortableContext);

  if (context === undefined) {
    throw new Error('useSortable must be used within a SortableProvider');
  }

  return context;
}
